import type { ReadinessCheck } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export interface StoreErrorTarget {
  field: 'nextOrderNumber' | 'defaultCurrency' | 'root.server';
  key: string;
  values?: Record<string, unknown>;
}

/**
 * Store PATCH refusals that deserve their own words: the order counter moving backwards (409
 * with `meta.current`), a currency other records already depend on (409 STORE_CURRENCY_LOCKED)
 * and a closed store (409 STORE_READ_ONLY).
 */
export function storeSaveErrorTarget(error: unknown): StoreErrorTarget | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'STORE_CURRENCY_LOCKED') {
    return { field: 'defaultCurrency', key: 'stores:errors.currencyLocked' };
  }
  if (error.code === 'STORE_READ_ONLY')
    return { field: 'root.server', key: 'stores:errors.readOnly' };
  if (error.kind === 'conflict' && typeof error.meta.current === 'number') {
    return {
      field: 'nextOrderNumber',
      key: 'stores:errors.nextOrderNumberBackwards',
      values: { current: error.meta.current },
    };
  }
  return null;
}

/** The failing blockers a 409 STORE_NOT_READY carries in `meta.checks`; null for any other error. */
export function activationBlockers(error: unknown): ReadinessCheck[] | null {
  if (!(error instanceof ApiError) || error.code !== 'STORE_NOT_READY') return null;
  const checks = error.meta.checks;
  if (!Array.isArray(checks)) return [];
  return checks.filter(
    (check): check is ReadinessCheck =>
      typeof check === 'object' &&
      check !== null &&
      typeof (check as ReadinessCheck).key === 'string' &&
      typeof (check as ReadinessCheck).message === 'string',
  );
}
